<script setup lang="ts">
import { computed } from "vue";
import type { ActionsWithSharedBordersProps } from "./tables.interfaces";
import SectionContainer from "@/views/blocks/shared/section/container/SectionContainer.vue";
import Title from "@/views/blocks/tables/shared/title/Title.vue";
import Description from "@/views/blocks/tables/shared/description/Description.vue";
import Icon from "@/views/blocks/tables/shared/icon/Icon.vue";
import type { PageBlock } from "@/views/blocks/PageBlock.interface";
import type { CssVarColors } from "@/shared/themes/CssVarColors.interface";
import EditableElement from "@/views/page/EditableElement.vue";

const props = defineProps<{
    block: PageBlock<ActionsWithSharedBordersProps>;
    pageColors: CssVarColors;
}>();

const dataValues = computed(() => props.block.dataValues);
const styleValues = computed(() => props.block.styleValues);
</script>

<template>
    <SectionContainer
        :id="block.webId"
        class="lg:px-8 px-6"
        :backgroundColor="styleValues.background"
    >
        <div class="mx-auto max-w-7xl px-6 lg:px-8">
            <div class="rounded-xl shadow-lg overflow-hidden border border-gray-200">
                <div class="grid grid-cols-1 sm:grid-cols-2">
                    <EditableElement
                        v-for="(action, index) in dataValues.actions.data.items"
                        :key="action.id"
                        :blockId="block.id"
                        :field-name="`actions_${action.id}`"
                        :hideable="false"
                    >
                        <a
                            :href="action.link"
                            target="_blank"
                            rel="noopener"
                            :class="[
                                'group relative p-8 cursor-pointer',
                                'border-b border-gray-200 sm:border-r',
                                'focus:outline-none focus:ring-2 focus:ring-inset',
                                {
                                    'sm:border-r-0': index % 2 === 1,
                                    'rounded-t-xl sm:rounded-tl-xl sm:rounded-tr-none': index === 0,
                                    'sm:rounded-tr-xl sm:rounded-tl-none': index === 1,
                                    'rounded-b-xl':
                                        index === dataValues.actions.data.items.length - 1,
                                    'sm:rounded-bl-xl sm:rounded-br-none sm:border-b-0':
                                        (index === dataValues.actions.data.items.length - 2 &&
                                            dataValues.actions.data.items.length % 2 === 0) ||
                                        (index === dataValues.actions.data.items.length - 1 &&
                                            dataValues.actions.data.items.length % 2 === 1),
                                    'rounded-b-xl sm:rounded-br-xl sm:rounded-bl-none border-b-0':
                                        index === dataValues.actions.data.items.length - 1 &&
                                        dataValues.actions.data.items.length % 2 === 0,
                                },
                            ]"
                            :style="{
                                '--tw-ring-color': styleValues.brand,
                                backgroundColor: styleValues.background,
                            }"
                        >
                            <div class="flex flex-col h-full">
                                <Icon
                                    :src="action.imageSrc"
                                    :alt="action.imageAlt"
                                    :backgroundColor="styleValues.brand"
                                    class="mb-8"
                                />
                                <Title
                                    :text="action.title"
                                    :textColor="styleValues.text"
                                    class="mb-2"
                                />
                                <Description
                                    :text="action.description"
                                    :textColor="styleValues.text"
                                />
                            </div>
                            <span
                                class="pointer-events-none absolute right-8 top-8 text-gray-300 group-hover:text-gray-400"
                                aria-hidden="true"
                            >
                                <svg class="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                                    <path
                                        d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z"
                                    />
                                </svg>
                            </span>
                        </a>
                    </EditableElement>
                </div>
            </div>
        </div>
    </SectionContainer>
</template>
